import React, { SetStateAction, useEffect } from 'react';
import { Button, Form, Modal, Radio, RadioChangeEvent, Select, Space, Switch } from 'antd';

import { useDivisionsQuery } from 'app/services/edpApiV1/viewer';
import { useAppDispatch, useAppSelector } from 'app/store';

import { uiModel } from 'entities/ui';

import { TVSSearchParams } from 'shared/types/types';

import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import style from './index.module.scss';

type TProps = {
  setIsModalOpen: React.Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  setCurrentPage: React.Dispatch<SetStateAction<number>>;
  emplyeesQueryTriger: (params: TVSSearchParams) => void;
  currentYear: number;
  currentSize: number;
  currentSearchValue: string | undefined;
};

const ModalFilter = ({
  isModalOpen,
  setIsModalOpen,
  setCurrentPage,
  emplyeesQueryTriger,
  currentYear,
  currentSize,
  currentSearchValue,
}: TProps) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const {
    divisionId,
    filterEmployees: filterEmployeesValue,
    filterRequestStatuses,
    active,
    currentDiuvisionId,
  } = useAppSelector(uiModel.VacationSchedule.selectors.selectVacationSchedule);
  // нужно для хранения значения подразделения при кейсе с удалением значения "Поздразделение" формы но не подтверждая
  // const [formDivisionId, setFormDivisionId] = useState('');
  const { data } = useDivisionsQuery(null);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const onSelectDivision = (id: string) => {
    dispatch(uiModel.VacationSchedule.actions.divisionId(id));
    dispatch(uiModel.VacationSchedule.actions.currentDivisionId(id));
  };

  const onClearDivision = () => {
    dispatch(uiModel.VacationSchedule.actions.divisionId(''));
  };

  const onChangeRadio = (e: RadioChangeEvent) => {
    dispatch(uiModel.VacationSchedule.actions.filterEmployees(e.target.value));
  };

  const onChangeFilterRequestStatus = (value: boolean) => {
    dispatch(uiModel.VacationSchedule.actions.filterRequestStatuses(value));
  };

  const onSave = () => {
    const querryParams: TVSSearchParams = {
      year: currentYear,
      size: currentSize,
      page: 1,
      filterEmployees: filterEmployeesValue,
      filterRequestStatuses: filterRequestStatuses ? ['consideredByHR'] : undefined,
      divisionId: divisionId ? divisionId : undefined,
      search: currentSearchValue ? currentSearchValue : undefined,
      allAvailableOrganizations,
    };
    emplyeesQueryTriger(querryParams);
    dispatch(uiModel.VacationSchedule.actions.active(true));
    setIsModalOpen(false);
    setCurrentPage(1);
    dispatch(uiModel.VacationSchedule.actions.currentDivisionId(divisionId));
    if (divisionId === '' && filterEmployeesValue === 'all' && !filterRequestStatuses && active) {
      dispatch(uiModel.VacationSchedule.actions.active(false));
    }
  };

  const onResetFilters = () => {
    const querryParams: TVSSearchParams = {
      year: currentYear,
      size: currentSize,
      page: 1,
      filterEmployees: 'all',
      allAvailableOrganizations,
    };
    dispatch(uiModel.VacationSchedule.actions.resetFilters());
    form.setFields([
      {
        name: 'filterEmployees',
        value: 'all',
      },
      {
        name: 'filterRequestStatuses',
        value: false,
      },
      {
        name: 'divisionId',
        value: '',
      },
    ]);
    emplyeesQueryTriger(querryParams);
    setIsModalOpen(false);
  };

  useEffect(() => {
    form.setFields([
      {
        name: 'filterEmployees',
        value: filterEmployeesValue,
      },
      {
        name: 'filterRequestStatuses',
        value: filterRequestStatuses,
      },
      {
        name: 'divisionId',
        value: divisionId,
      },
    ]);
    // чищу при размонтировании
    return () => {
      dispatch(uiModel.VacationSchedule.actions.resetFilters());
      form.setFields([
        {
          name: 'filterEmployees',
          value: 'all',
        },
        {
          name: 'filterRequestStatuses',
          value: false,
        },
        {
          name: 'divisionId',
          value: '',
        },
      ]);
    };
  }, []);

  // нужно для инициализации поля "Подразделение" при открытии модального окна,
  //  предварительно удалив divisionId нажав на крестик в селекте формы,
  //  но закрыли модальное окно не нажав "Применить"
  useEffect(() => {
    if (isModalOpen && currentDiuvisionId && divisionId === '') {
      dispatch(uiModel.VacationSchedule.actions.divisionId(currentDiuvisionId));
      form.setFields([
        {
          name: 'divisionId',
          value: currentDiuvisionId,
        },
      ]);
    } else {
      form.setFields([
        {
          name: 'divisionId',
          value: divisionId,
        },
      ]);
      form.setFields([
        {
          name: 'filterRequestStatuses',
          value: filterRequestStatuses,
        },
      ]);
      form.setFields([
        {
          name: 'filterEmployees',
          value: filterEmployeesValue,
        },
      ]);
    }
  }, [isModalOpen]);

  return (
    <Modal
      visible={isModalOpen}
      forceRender
      title="Настройка фильтров"
      footer={false}
      onCancel={() => setIsModalOpen(false)}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="divisionId" label="Подразделение">
          <Select
            showSearch
            placeholder="Выберите подразделение"
            onSelect={onSelectDivision}
            allowClear
            onClear={onClearDivision}
            optionFilterProp="children"
            filterOption={(input: string, option: { key: string; children: string; value: string } | undefined) =>
              (option?.children ?? '').toLocaleLowerCase().includes(input)
            }
          >
            {data?.data &&
              data?.data?.map(({ id, name }) => {
                return (
                  <Select.Option value={id} key={id}>
                    {name}
                  </Select.Option>
                );
              })}
          </Select>
        </Form.Item>

        <Form.Item name="filterEmployees">
          <Radio.Group onChange={onChangeRadio} value={filterEmployeesValue}>
            <Space direction="vertical">
              <Radio value="all">Все сотрудники</Radio>
              <Radio value="withRequests">Сотрудники с заявками</Radio>
              <Radio value="withoutRequests">Сотрудники без заявок</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="filterRequestStatuses" label="На рассмотрении у HR">
          <Switch checked={filterRequestStatuses} onChange={onChangeFilterRequestStatus} />
        </Form.Item>

        <div className={style.BtnsGroup}>
          <Button type="primary" onClick={onSave}>
            Применить
          </Button>

          <Button type="primary" onClick={onResetFilters}>
            Сбросить фильтры
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalFilter;
